import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Workflow, Check, ShieldCheck } from "lucide-react";
import useScrollReveal from "../../hooks/useScrollReveal";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import StickyMobileCTA from "../site/StickyMobileCTA";
import MethodSection from "../site/MethodSection";
import MonthlySupport from "../site/MonthlySupport";
import {
  PackageGrid,
  ProcessSection,
  FAQSection,
} from "../site/OfferingPrimitives";

const openAuditWithOffer = (offer) =>
  window.dispatchEvent(
    new CustomEvent("open-audit", { detail: { offer } })
  );

const openRevenueAudit = () => openAuditWithOffer("Revenue Audit");

/* ---------- Data ---------- */

const implementationPackages = [
  {
    id: "lead-capture-system",
    title: "Lead Capture System",
    price: "$2,000",
    priceLabel: "Setup",
    description:
      "For businesses that need a stronger way to capture, organize, and respond to new leads.",
    builtFor: [
      "Businesses with no clean intake process",
      "Businesses collecting leads manually",
      "Owners relying on DMs, texts, calls, or scattered forms",
      "Service providers who need a simple system before deeper automation",
    ],
    includes: [
      "Lead intake form",
      "Thank-you page",
      "Basic CRM setup or lead sheet",
      "Email notification handoff",
      "Basic source tracking",
      "Lead routing logic",
      "Form testing",
      "Launch walkthrough",
    ],
    cta: "Build My Lead Capture",
  },
  {
    id: "follow-up-booking-system",
    title: "Follow-Up + Booking System",
    price: "$4,250",
    priceLabel: "Setup",
    badge: "Best for Service Businesses",
    description:
      "For businesses that need leads to move from interest to booked appointments faster.",
    builtFor: [
      "Businesses getting leads but not enough bookings",
      "Appointment-based businesses",
      "Coaches, trainers, consultants",
      "Local service providers",
      "Teams losing leads after first contact",
    ],
    includes: [
      "Lead capture flow",
      "CRM pipeline structure",
      "Booking link integration",
      "Email follow-up sequence",
      "SMS follow-up logic, if available",
      "Missed lead notification setup",
      "Source tracking",
      "Thank-you page",
      "Basic reporting view",
      "Launch testing",
    ],
    cta: "Build My Booking System",
    emphasized: true,
  },
  {
    id: "revenue-conversion-system",
    title: "Revenue Conversion System",
    price: "$10,000",
    priceLabel: "Setup",
    description:
      "For businesses that need a complete lead-to-cash system built around capture, follow-up, booking, tracking, and conversion.",
    builtFor: [
      "Businesses running ads",
      "Businesses with multiple offers",
      "Businesses with old leads or past client lists",
      "Brands needing landing page + CRM + booking + follow-up + tracking connected",
      "Service businesses ready for a full front-end revenue system",
    ],
    includes: [
      "AI-assisted landing page or funnel page",
      "Lead capture system",
      "CRM pipeline setup",
      "Booking flow",
      "Email/SMS follow-up automation",
      "Lead source tracking",
      "Reactivation campaign setup",
      "Review request flow",
      "Reporting dashboard",
      "Internal notification handoff",
      "Launch testing",
      "Optimization walkthrough",
    ],
    cta: "Build My Revenue System",
  },
];

const auditIncludes = [
  "Website conversion review",
  "Lead capture review",
  "Booking flow review",
  "Follow-up review",
  "CRM and automation opportunity map",
  "Prioritized revenue leak report",
  "Recommended implementation path",
];

const auditBuiltFor = [
  "Businesses getting leads but not enough bookings",
  "Owners unsure why their website is not converting",
  "Service providers with unclear follow-up systems",
  "Businesses that know revenue is leaking but do not know where",
];

const faqs = [
  {
    q: "What is the Revenue Audit?",
    a: "A diagnostic review that identifies where your business is leaking leads, bookings, follow-up, and revenue. It reviews your website, forms, follow-up, booking flow, CRM setup, and automation opportunities, and delivers a prioritized leak report with the recommended implementation path.",
  },
  {
    q: "How does the $497 audit credit work?",
    a: "If you move forward with one of the three implementation packages within 7 days of receiving your audit, the $497 audit fee is credited toward the build. The audit pays for itself if you continue.",
  },
  {
    q: "Do I have to buy a package after the audit?",
    a: "No. The Revenue Audit is a standalone diagnostic. You'll leave with a clear report you can implement yourself, hand to another team, or use to decide which implementation package fits best.",
  },
  {
    q: "How do the three implementation packages differ?",
    a: "Lead Capture System ($2,000) installs a clean intake process. Follow-Up + Booking System ($4,250) adds CRM, booking flow, and follow-up automation on top. Revenue Conversion System ($10,000) is the full lead-to-cash build — landing page, CRM, follow-up, tracking, reactivation, review flow, and reporting.",
  },
  {
    q: "How long does each build take?",
    a: "Lead Capture: 1–2 weeks. Follow-Up + Booking: 2–3 weeks. Revenue Conversion System: 3–4 weeks. Timelines depend on how quickly assets and tool access are provided.",
  },
  {
    q: "Which CRM and booking tools do you support?",
    a: "GoHighLevel, HubSpot, Calendly, Acuity, Square Appointments, Setmore, Google Calendar, Mailchimp, Airtable, Notion, and most modern stacks. If your tools are not on this list, the Revenue Audit will recommend the cleanest fit.",
  },
];

/* ---------- Page ---------- */

export default function RevenueSystemsPage() {
  useScrollReveal();
  const navigate = useNavigate();

  return (
    <div
      className="bg-[#0F0F0E] text-[#F4F0E8] min-h-screen"
      data-testid="revenue-systems-page"
    >
      <SEO page="/revenue-systems" />
      <Nav />
      <main>
        <RevenueHero />
        <MethodSection tone="dark" />
        <RevenueAuditStandalone />
        <TransitionBlock />
        <PackageGrid
          id="implementation-packages"
          tone="dark"
          eyebrow="Implementation Packages"
          title="Three builds. One clear path."
          subtitle="After the audit, the recommendation maps to one of these three implementation levels."
          packages={implementationPackages}
          onCta={(pkg) => openAuditWithOffer(pkg.title)}
        />
        <MonthlySupport tone="dark" onCtaClick={openRevenueAudit} />
        <ProcessSection
          tone="dark"
          eyebrow="How It Works"
          title="From lead leak to working revenue system."
        />
        <FAQSection tone="dark" faqs={faqs} />
        <FinalCTA navigate={navigate} />
      </main>
      <Footer />
      <StickyMobileCTA
        label="Book Revenue Audit"
        onClick={openRevenueAudit}
        testId="revenue-sticky-cta"
      />
    </div>
  );
}

/* ---------- Sections ---------- */

function RevenueHero() {
  return (
    <section
      data-testid="revenue-hero"
      className="pt-24 sm:pt-28 md:pt-32 pb-16 sm:pb-20 md:pb-24 px-5 sm:px-6 md:px-10 bg-[#0F0F0E]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#3C3935] bg-[#151514] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-[#A56A43]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#A56A43]" />
                Modern Exit Revenue Systems
              </span>
              <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl border border-[#3C3935] bg-[#151514] text-[#F4F0E8]">
                <Workflow size={15} strokeWidth={1.6} />
              </span>
            </div>
            <h1
              data-kinetic
              data-testid="revenue-headline"
              className="font-display mt-6 text-[2.1rem] sm:text-[2.6rem] md:text-5xl lg:text-[3.6rem] xl:text-[4rem] leading-[1.05] tracking-[-0.02em] font-semibold text-[#F4F0E8]"
            >
              Fix the system{" "}
              <span className="italic text-[#BEB8AF] font-normal">
                behind your website.
              </span>
            </h1>
          </div>
          <div className="lg:col-span-4 reveal">
            <p className="text-base md:text-lg text-[#BEB8AF] leading-relaxed">
              Start with the Revenue Audit — a $497 diagnostic that maps where
              leads, bookings, follow-up, and revenue are leaking. Then pick
              the implementation package that matches what needs to be
              rebuilt.
            </p>
          </div>
        </div>

        <div className="mt-10 sm:mt-12 reveal flex justify-center">
          <button
            type="button"
            onClick={openRevenueAudit}
            data-testid="revenue-primary-cta"
            data-magnetic
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-[#F4F0E8] border-[1.5px] border-[#3C3935] px-7 py-4 text-[15px] font-medium hover:bg-[#1A1A18] hover:border-[#A56A43] transition-colors min-h-[52px]"
          >
            Start Revenue Audit
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>
      </div>
    </section>
  );
}

function RevenueAuditStandalone() {
  return (
    <section
      id="revenue-audit"
      data-testid="revenue-audit-standalone"
      className="scroll-mt-24 sm:scroll-mt-28 py-16 sm:py-20 md:py-24 px-5 sm:px-6 md:px-10 bg-[#0F0F0E] text-[#F4F0E8]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10 sm:mb-12 reveal">
          <div className="lg:col-span-7">
            <p className="eyebrow !text-[#A56A43]">Diagnostic · Start Here</p>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-[-0.02em] font-semibold text-[#F4F0E8]">
              Revenue Audit.{" "}
              <span className="italic text-[#BEB8AF] font-normal">
                The front door.
              </span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-[#BEB8AF] leading-relaxed">
              A diagnostic review that identifies where your business is
              leaking leads, bookings, follow-up, and revenue. Complete it
              first — then choose or be recommended one of the three
              implementation packages.
            </p>
          </div>
        </div>

        <div
          className="reveal rounded-[36px] border border-[#3C3935] bg-[#151514] p-7 sm:p-10 md:p-12 relative shadow-[0_40px_90px_-40px_rgba(0,0,0,0.55)]"
          data-testid="revenue-audit-card"
        >
          <span className="absolute -top-3 left-7 sm:left-10 md:left-12 inline-flex items-center gap-1.5 rounded-full bg-[#A56A43] text-[#F4F0E8] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.28em]">
            <ShieldCheck size={11} strokeWidth={2} />
            Standalone Offer
          </span>

          <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-start">
            <div className="md:col-span-7">
              <h3 className="font-display text-2xl sm:text-3xl md:text-[2.2rem] leading-tight tracking-[-0.02em] font-semibold text-[#F4F0E8]">
                Revenue Audit
              </h3>
              <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                <span className="font-display text-[2rem] sm:text-[2.4rem] md:text-[2.6rem] font-semibold tracking-[-0.02em] leading-none text-[#F4F0E8]">
                  $497
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E887F]">
                  Diagnostic Fee
                </span>
              </div>
              <p className="mt-5 text-[15px] sm:text-base text-[#BEB8AF] leading-relaxed max-w-xl">
                A diagnostic review that identifies where your business is
                leaking leads, bookings, follow-up, and revenue.
              </p>
              <p className="mt-3 text-[14.5px] sm:text-[15px] text-[#BEB8AF] leading-relaxed max-w-xl">
                We review your website, lead capture, booking flow, follow-up
                process, and CRM structure, then provide a prioritized revenue
                leak report and recommended implementation path.
              </p>
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#A56A43]/40 bg-[#A56A43]/[0.10] px-3.5 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#A56A43]" />
                <p className="text-[12.5px] sm:text-[13px] text-[#F4F0E8] leading-tight">
                  Move forward with a system build within 7 days and the $497
                  audit fee is credited toward implementation.
                </p>
              </div>

              <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.28em] text-[#A56A43]">
                Built for
              </p>
              <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {auditBuiltFor.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-[14px] text-[#BEB8AF] leading-relaxed"
                  >
                    <span className="text-[#8E887F]">·</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={openRevenueAudit}
                data-testid="revenue-audit-cta"
                className="mt-9 group inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-[#F4F0E8] border-[1.5px] border-[#3C3935] px-7 py-4 text-[15px] font-medium hover:bg-[#1A1A18] hover:border-[#A56A43] transition-colors min-h-[52px]"
              >
                Start Revenue Audit
                <ArrowRight
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5"
                />
              </button>
            </div>

            <div className="md:col-span-5 md:border-l md:border-[#33302D] md:pl-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8E887F]">
                Includes
              </p>
              <ul className="mt-4 space-y-2.5">
                {auditIncludes.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-3 text-[14px] text-[#F4F0E8] leading-relaxed"
                  >
                    <span className="mt-0.5 h-5 w-5 grid place-items-center rounded-full bg-[#B1744B]/15 text-[#B1744B] shrink-0">
                      <Check size={11} strokeWidth={2.4} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function TransitionBlock() {
  return (
    <section
      data-testid="audit-to-packages-transition"
      className="py-16 sm:py-20 md:py-24 px-5 sm:px-6 md:px-10 bg-[#0F0F0E] text-[#F4F0E8]"
    >
      <div className="max-w-4xl mx-auto text-center reveal">
        <p className="eyebrow !text-[#A56A43]">After The Audit</p>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-[#F4F0E8]">
          We build the system{" "}
          <span className="italic text-[#BEB8AF] font-normal">
            that fixes the leak.
          </span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#BEB8AF] leading-relaxed max-w-2xl mx-auto">
          Choose the implementation level that matches how much of your
          lead-to-revenue process needs to be repaired or rebuilt.
        </p>
      </div>
    </section>
  );
}

function FinalCTA({ navigate }) {
  return (
    <section
      id="contact"
      data-testid="revenue-final-cta"
      className="scroll-mt-24 sm:scroll-mt-28 py-20 sm:py-24 md:py-28 px-5 sm:px-6 md:px-10 bg-[#0F0F0E] text-[#F4F0E8]"
    >
      <div className="max-w-3xl mx-auto text-center reveal">
        <p className="eyebrow !text-[#A56A43]">Final Step</p>
        <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3rem] leading-[1.05] tracking-[-0.02em] font-semibold text-[#F4F0E8]">
          Ready to plug{" "}
          <span className="italic text-[#BEB8AF] font-normal">
            the revenue leak?
          </span>
        </h2>
        <p className="mt-6 text-base md:text-lg text-[#BEB8AF] leading-relaxed max-w-xl mx-auto">
          Start with a Revenue Audit. We will map exactly where your business
          is losing leads, bookings, and revenue — and which implementation
          package fixes it first.
        </p>
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={openRevenueAudit}
            data-testid="revenue-final-primary-cta"
            data-magnetic
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-[#F4F0E8] border-[1.5px] border-[#3C3935] px-7 py-4 text-[15px] font-medium hover:bg-[#1A1A18] hover:border-[#A56A43] transition-colors min-h-[52px]"
          >
            Start Revenue Audit
            <ArrowRight
              size={16}
              className="transition-transform group-hover:translate-x-0.5"
            />
          </button>
        </div>

        <button
          type="button"
          onClick={() => navigate("/packages")}
          data-testid="revenue-back-to-website"
          className="mt-10 inline-flex items-center gap-2 text-[14px] text-[#8E887F] hover:text-[#F4F0E8] transition-colors"
        >
          <ArrowLeft size={14} />
          Back to Website Packages
        </button>
      </div>
    </section>
  );
}
